import { ReactNode } from "react";
import { Box, Text, Heading, useColorModeValue, Button } from "@chakra-ui/react";
import { Link } from "react-router-dom";

import { Overline } from "../components";
import { CollecticonArrowLeft, CollecticonUsers } from "@devseed-ui/collecticons-chakra";
import { InferenceCollection } from "../types/inferences";
import SceneOptionsMenu from "./SceneOptionsMenu";

type PanelHeaderProps = {
  sceneDate: string;
  closeLink: string;
  closeLabel: string;
  children: ReactNode;
  inferences: InferenceCollection | undefined;
  onDeleteScene?: () => void;
  onShareScene?: () => void;
  sceneId?: string;
  userRole: "owner" | "collaborator";
  isSceneShared?: boolean | null;
};

export const PanelHeader = ({
  sceneDate,
  closeLink,
  closeLabel,
  children,
  onDeleteScene,
  onShareScene,
  isSceneShared,
  userRole,
  inferences,
  sceneId,
}: PanelHeaderProps) => {
  return (
    <Box px={6} pt={4}>
      <Box display="flex" gap="2" justifyContent="space-between">
        <Box flex="1" display="flex" flexDirection="column" gap="1">
          <Link
            to={closeLink}
            aria-label={closeLabel}
            title={closeLabel}
            role="group"
          >
            <Overline
              display="flex"
              fontFamily="body"
              gap="3"
              alignItems="center"
              _hover={{
                color: useColorModeValue("primary.700", "primary.300"),
              }}
            >
              <CollecticonArrowLeft size="3" />
              <Text
                as="span"
                transition="color 0.2s"
                _groupHover={{
                  textDecoration: "underline",
                }}
              >
                My scenes
              </Text>
              <span>/</span>
              {sceneDate.slice(0, 20).replace(/,/g, "")}
            </Overline>
          </Link>
          <Heading as="h1" fontSize="lg" mt="1">
            {sceneDate}
          </Heading>
        </Box>
        <Box display="flex" gap="1" alignItems="center">
          <Button
            size="xs"
            ml="auto"
            display="inline-flex"
            gap="1"
            px={isSceneShared ? 2 : 0}
            variant={isSceneShared ||userRole === "collaborator" ? "primary-soft-outline" : "base-soft-outline"}
            bg={isSceneShared ||userRole === "collaborator" ? "primary.200a" : "transparent"}
            title="Scene Options"
            onClick={onShareScene}
            aria-label="Share Scene"
          >
            {isSceneShared && "Shared"}
            <CollecticonUsers size="3" />
          </Button>
          <SceneOptionsMenu
            sceneDate={sceneDate}
            inferences={inferences}
            onDeleteScene={onDeleteScene}
            onShareScene={onShareScene}
            userRole={userRole}
            sceneId={sceneId}
          />
        </Box>
      </Box>
      {children}
    </Box>
  );
};
type PanelBodyProps = {
  children: React.ReactNode;
};
export const PanelBody = ({ children }: PanelBodyProps) => {
  return (
    <Box display="flex" flexDirection="column" gap="6" p={6}>
      {children}
    </Box>
  );
};
type PanelFooterProps = {
  children: React.ReactNode;
};
export const PanelFooter = ({ children }: PanelFooterProps) => {
  const bgColor = useColorModeValue("base.50", "base.700");
  return (
    <Box
      display="flex"
      bg={bgColor}
      position="sticky"
      flexShrink={0}
      bottom={0}
      flexDirection="column"
      gap="2"
      mt="auto"
      p={6}
    >
      {children}
    </Box>
  );
};

type PanelProps = {
  children: React.ReactNode;
};
function Panel({ children }: PanelProps) {
  const borderColor = useColorModeValue("base.100a", "base.500");
  return (
    <Box
      overflow="auto"
      display="flex"
      flexDirection="column"
      boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)"
      borderRight="1px solid"
      borderColor={borderColor}
    >
      {children}
    </Box>
  );
}
export default Panel;
